import {
  Avatar,
  Box,
  ButtonBase,
  Card,
  CardActionArea,
  List,
  ListItem,
  Skeleton,
  Stack,
  Theme,
  Typography,
} from '@mui/material'
import get from 'lodash/get'
import { Fade, ItemImageArea, SkeletonText } from 'utoniq-ui'

import {
  Artist,
  EntryCampaignRankingConnectionFragment,
  Maybe,
  PortfoliosConnectionFragment,
  UserCommonWithCustomProfileConnectionFragment,
  UserProfileQuery,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, Header } from '@/components/layouts'
import {
  CampaignBoxSkeleton,
  CollectionBox,
  CollectionBoxSkeleton,
  ItemBoxSkeleton,
} from '@/components/organisms'
import { HorizontalList } from '@/components/organisms/functionals'
import { Rank } from '@/components/organisms/ranking/Rank'
import { styles } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import {
  Cell,
  CellFrame,
  Organism,
  OrganismFrame,
  Template,
} from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  userId: string
  user: Maybe<UserCommonWithCustomProfileConnectionFragment>
  isCustom: boolean
  items: Maybe<PortfoliosConnectionFragment>
  rankings: Maybe<Array<Maybe<EntryCampaignRankingConnectionFragment>>>
  collections: Maybe<UserProfileQuery['ownedUserCollections']['edges']>
  organizationArtistRankings: Maybe<
    UserProfileQuery['organizationArtistRankings']['edges']
  >
}

type UserCellProps = {
  loading: State['loading']
  user: State['user']
}

type UserOwnedItemCellProps = {
  loading: State['loading']
  items: State['items']
  userId: string
}

type CampaignRankingCellProps = {
  loading: State['loading']
  rankings: State['rankings']
}

type UserCollectionCellProps = {
  loading: State['loading']
  userId: State['userId']
  collections: State['collections']
}

type UserCustomProfileProps = {
  user: State['user']
  isCustom: State['isCustom']
}

export type Action = {}

export const UserProfileTemplate: Template<State, Action> = ({ state }) => {
  const { routerNames } = useRouter()

  return (
    <BaseLayout safeArea={{ pt: 0 }}>
      <Head title={state.user?.displayName || routerNames.UserProfile} />
      <UserCustomProfile isCustom={state.isCustom} user={state.user} />
      <UserInfo user={state.user} loading={state.loading} />
      <ArtistRankingCell
        rankings={state.organizationArtistRankings}
        loading={state.loading}
      />
      <CampaignRankingCell rankings={state.rankings} loading={state.loading} />
      <UserCollectionCell
        userId={state.userId}
        collections={state.collections}
        loading={state.loading}
      />
      <UserOwnedItemCell
        items={state.items}
        userId={state.user?.id ?? ''}
        loading={state.loading}
      />
    </BaseLayout>
  )
}

const UserInfo: Cell<UserCellProps> = ({ user, loading }) => {
  if (loading) {
    return (
      <Stack sx={{ py: 2, px: 5 }} spacing={3} alignItems="center" flex={1}>
        <Skeleton variant="circular" width={64} height={64} />
        <Stack sx={{ minWidth: 84 }}>
          <SkeletonText variant="medium" width="100%" />
          <SkeletonText variant="small" width="100%" />
        </Stack>
        <Stack sx={{ width: '100%' }} alignItems="center" spacing={1}>
          <SkeletonText variant="small" numberOfLine={2} lastWidth="50%" />
        </Stack>
      </Stack>
    )
  }
  return (
    <CellFrame sx={{ px: 0, mb: 8 }}>
      <Stack justifyContent="center" alignItems="center" sx={{ px: 5 }}>
        <Avatar
          sx={{ width: 64, height: 64 }}
          src={user?.avatar?.smallUri || undefined}
        />
        <Typography variant="h4" sx={{ mt: 3 }}>
          {user?.displayName}
        </Typography>
        <Typography variant="overline" color="text.hint">
          {user?.formattedAccountId}
        </Typography>
        <Typography variant="caption" sx={{ mt: 3 }}>
          {user?.introduction}
        </Typography>
      </Stack>
    </CellFrame>
  )
}

const CampaignRankingCell: Cell<CampaignRankingCellProps> = ({
  rankings,
  loading,
}) => {
  const { t } = useTranslation('userProfile')
  if (!config.modules.campaign) return null

  return (
    <CellFrame sx={{ px: 5, pb: 4 }}>
      <Typography variant="h5" sx={{ py: 2, color: 'grey.600' }}>
        {t('joinCampaign')}
      </Typography>
      {loading ? (
        <Stack sx={{ pt: 2 }}>
          {[1, 2, 3].map(i => (
            <CampaignBoxSkeleton key={i} />
          ))}
        </Stack>
      ) : (
        <List>
          {!!rankings && rankings?.length > 0 ? (
            rankings?.map(ranking => (
              <ListItem key={ranking?.id} sx={{ p: 0, my: 2 }}>
                <Avatar
                  variant="square"
                  sx={{
                    width: 100,
                    height: 64,
                    borderRadius: 2,
                  }}
                  alt={ranking?.myRank?.displayName}
                  src={ranking?.thumbnail?.smallUri || undefined}
                />
                <Stack sx={{ pl: 4, width: '100%' }}>
                  <Typography variant="h2" sx={{ pb: 1 }}>
                    {ranking?.myRank?.rank}
                    <Typography
                      variant="subtitle2"
                      component="p"
                      display="inline"
                    >
                      {t('rank')}
                    </Typography>
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-end"
                  >
                    <Typography variant="body1">
                      {ranking?.myRank?.displayName}
                    </Typography>
                    <Typography variant="body2" color="text.hint">
                      {ranking?.myRank?.point}pt
                    </Typography>
                  </Stack>
                </Stack>
              </ListItem>
            ))
          ) : (
            <Typography variant="body1" color="text.secondary" align="center">
              {t('campaignNotExist')}
            </Typography>
          )}
        </List>
      )}
    </CellFrame>
  )
}

const UserOwnedItemCell: Cell<UserOwnedItemCellProps> = ({
  items,
  userId,
  loading,
}) => {
  const { t } = useTranslation('userProfile')
  const { generatePath, routerPages } = useRouter()
  return (
    <CellFrame
      sx={{
        pt: items?.edges.length ? 10 : 0,
        mb: 8,
      }}
    >
      <HorizontalList
        loading={loading}
        loadingItemLength={6}
        title={t('ownedItems')}
        titleTypographyProps={{ variant: 'h5', color: 'grey.600' }}
        listHref={
          userId && generatePath(routerPages.UserOwnedItems, { userId })
        }
        items={items?.edges ?? []}
        trans={t('toList')}
        LoadingComponent={ItemBoxSkeleton}
        renderItem={({ node: { item } }, index) => (
          <Card key={index}>
            <CardActionArea
              href={generatePath(routerPages.UserItemDetail, {
                userId,
                itemId: item.id,
              })}
            >
              <ItemImageArea src={item.thumbnail?.front?.smallUri ?? ''} />
            </CardActionArea>
          </Card>
        )}
        EmptyComponent={
          <Typography variant="body1" color="text.secondary" align="center">
            {t('itemNotExist')}
          </Typography>
        }
      />
    </CellFrame>
  )
}

const UserCollectionCell: Cell<UserCollectionCellProps> = ({
  collections,
  userId,
  loading,
}) => {
  const { t } = useTranslation('userProfile')
  return (
    <HorizontalList
      loading={loading}
      loadingItemLength={6}
      title={t('collection')}
      titleTypographyProps={{ variant: 'h5', color: 'grey.600' }}
      items={collections ?? []}
      LoadingComponent={CollectionBoxSkeleton}
      renderItem={({ node }, index) =>
        node.collection && (
          <CollectionBox
            key={index}
            collection={node}
            userId={userId}
            sx={{ height: 156 }}
          />
        )
      }
      EmptyComponent={
        <Typography variant="body1" color="text.secondary" align="center">
          {t('collectionNotExist')}
        </Typography>
      }
    />
  )
}

export const UserCustomProfile: Organism<UserCustomProfileProps> = ({
  isCustom,
  user,
}) => {
  const avatarUrl = get(
    user,
    'customProfile.avatarItem.avatarResources.avatarResource.smallUri',
    null
  )
  const wallpaperUrl = get(
    user,
    'customProfile.wallpaperItem.wallpaperResources.wallpaperResource.mediumUri',
    null
  )

  return (
    <OrganismFrame
      sx={
        isCustom
          ? {
              position: 'relative',
              width: '100%',
              height: '400px',
              mb: 5,
            }
          : {
              height: (theme: Theme) => theme.mixins.toolbar.minHeight,
              mb: 3,
            }
      }
    >
      <Header
        back
        position="absolute"
        sx={{
          backgroundImage: 'unset',
          boxShadow: 'none',
          backgroundColor: 'transparent',
          pt: styles.safeAreaTop,
        }}
      />

      {wallpaperUrl && (
        <Fade>
          <Box
            id="wallpaper-img"
            component="img"
            src={wallpaperUrl}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        </Fade>
      )}
      {avatarUrl && (
        <Fade>
          <Box
            id="avatar-img"
            component="img"
            src={avatarUrl}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              m: 'auto',
              height: theme =>
                `calc(100% - ${theme.mixins.toolbar.minHeight}px)`,
            }}
          />
        </Fade>
      )}
    </OrganismFrame>
  )
}

type ArtistRankingCellProps = {
  loading: boolean
  rankings: State['organizationArtistRankings']
}
const ArtistRankingCell: Cell<ArtistRankingCellProps> = ({
  loading,
  rankings,
}) => {
  const { t } = useTranslation('userProfile')

  if (
    !loading &&
    !rankings?.filter(({ node }) => !!node.latestRankingPeriod)?.length
  ) {
    return null
  }

  return (
    <HorizontalList
      loading={loading}
      loadingItemLength={8}
      title={t('monthlyRanking')}
      titleTypographyProps={{ variant: 'h5', color: 'grey.600' }}
      TitleLoadingComponent={<SkeletonText variant="medium" width="25%" />}
      items={rankings ?? []}
      spacing={4}
      LoadingComponent={ArtistRankingSkeleton}
      renderItem={({ node }) =>
        node?.latestRankingPeriod && (
          <ArtistRanking
            key={node.latestRankingPeriod.id}
            rank={node.latestRankingPeriod.myRanking?.rank ?? 0}
            artistId={node.artistId}
            artist={node.artist}
          />
        )
      }
    />
  )
}

type ArtistRankingProps = {
  rank: number
  artistId: string
  artist?: Maybe<Pick<Artist, 'avatar'>>
}
const ArtistRanking: Cell<ArtistRankingProps> = ({
  rank,
  artist,
  artistId,
}) => {
  const { generatePath, routerPages } = useRouter()
  return (
    <CellFrame>
      <ButtonBase
        sx={{
          position: 'relative',
          width: 64,
          height: 64,
          borderRadius: 1,
        }}
        href={generatePath(routerPages.ArtistRanking, { artistId })}
      >
        <Rank
          size="medium"
          rank={rank}
          sx={{ transform: 'translateX(-4px)' }}
        />
        <Avatar
          sx={{
            width: 28,
            height: 28,
            position: 'absolute',
            bottom: 0,
            right: 0,
            zIndex: 3,
          }}
          src={artist?.avatar?.smallUri ?? ''}
        />
      </ButtonBase>
    </CellFrame>
  )
}

const ArtistRankingSkeleton = () => (
  <Box>
    <Stack
      justifyContent="center"
      sx={{ position: 'relative', width: 64, height: 64 }}
    >
      <Skeleton
        variant="rectangular"
        sx={{
          transform: 'translateX(8px) rotate(-45deg)',
          width: 'calc(56px/(0.707*2))',
          height: 'calc(56px/(0.707*2))',
        }}
      />
      <Skeleton
        variant="circular"
        sx={{
          width: 28,
          height: 28,
          position: 'absolute',
          bottom: 0,
          right: 0,
          zIndex: 3,
        }}
      />
    </Stack>
  </Box>
)
